// Geometry for correcting a site outline on the map. Rings are closed [lon, lat] lists; scaling and rotation
// work in local metres about the outline's own centroid, so they compose and can be undone exactly.

export type Ring = [number, number][];
export interface Position { lat: number; lon: number }
export interface OutlineAdjust { scale: number; rotationDeg: number }

export const NO_ADJUST: OutlineAdjust = { scale: 1, rotationDeg: 0 };
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;

const M_PER_DEG_LAT = 111_320;

export function ringCentre(ring: Ring): Position {
  const lons = ring.map(p => p[0]);
  const lats = ring.map(p => p[1]);
  return { lon: (Math.min(...lons) + Math.max(...lons)) / 2, lat: (Math.min(...lats) + Math.max(...lats)) / 2 };
}

export function shiftRing(ring: Ring, dLon: number, dLat: number): Ring {
  return ring.map(([lon, lat]) => [lon + dLon, lat + dLat]);
}

/** Area centroid of the outline, which any scale/rotate/shift of the outline carries along with it. */
export function ringPivot(ring: Ring): Position {
  const n = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring.length - 1 : ring.length;
  const [lon0, lat0] = ring[0];
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = [ring[i][0] - lon0, ring[i][1] - lat0];
    const [x2, y2] = [ring[(i + 1) % n][0] - lon0, ring[(i + 1) % n][1] - lat0];
    const cross = x1 * y2 - x2 * y1;
    a += cross;
    cx += (x1 + x2) * cross;
    cy += (y1 + y2) * cross;
  }
  if (Math.abs(a) < 1e-18) {
    const pts = ring.slice(0, n);
    return { lon: pts.reduce((s, p) => s + p[0], 0) / n, lat: pts.reduce((s, p) => s + p[1], 0) / n };
  }
  return { lon: lon0 + cx / (3 * a), lat: lat0 + cy / (3 * a) };
}

/** Radius of the circle around the centre that covers the whole outline, clamped to the search-area slider's range. */
export function ringRadiusM(ring: Ring, c: Position, min: number, max: number): number {
  const mLon = M_PER_DEG_LAT * Math.cos((c.lat * Math.PI) / 180);
  const r = Math.max(...ring.map(([lon, lat]) => Math.hypot((lon - c.lon) * mLon, (lat - c.lat) * M_PER_DEG_LAT)));
  return Math.min(max, Math.max(min, Math.ceil(r)));
}

/** Scales the points by `factor` and turns them `rotationDeg` clockwise (as on a compass), both about `pivot`. */
export function transformAbout(points: Ring, pivot: Position, factor: number, rotationDeg: number): Ring {
  const mLon = M_PER_DEG_LAT * Math.cos((pivot.lat * Math.PI) / 180);
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return points.map(([lon, lat]) => {
    const x = (lon - pivot.lon) * mLon;
    const y = (lat - pivot.lat) * M_PER_DEG_LAT;
    const rx = (x * cos + y * sin) * factor;
    const ry = (-x * sin + y * cos) * factor;
    return [pivot.lon + rx / mLon, pivot.lat + ry / M_PER_DEG_LAT];
  });
}

/**
 * The four corners (top-left, top-right, bottom-right, bottom-left as drawn on the plan) of the outline's bounding box in
 * the plan's own frame, which is turned `rotationDeg` clockwise on the map: where the handles to resize and turn it sit.
 */
export function turnedBox(ring: Ring, pivot: Position, rotationDeg: number): Ring {
  const upright = transformAbout(ring, pivot, 1, -rotationDeg);
  const lons = upright.map(p => p[0]);
  const lats = upright.map(p => p[1]);
  const [w, e, s, n] = [Math.min(...lons), Math.max(...lons), Math.min(...lats), Math.max(...lats)];
  return transformAbout([[w, n], [e, n], [e, s], [w, s]], pivot, 1, rotationDeg);
}

function groundOffset(p: Position, to: readonly [number, number]): [number, number] {
  const mLon = M_PER_DEG_LAT * Math.cos((p.lat * Math.PI) / 180);
  return [(to[0] - p.lon) * mLon, (to[1] - p.lat) * M_PER_DEG_LAT];
}

/** Degrees clockwise a drawing's top edge, from its top-left corner `a` to its top-right `b`, is turned from running east. */
export function topEdgeTurnDeg(a: readonly [number, number], b: readonly [number, number]): number {
  const [x, y] = groundOffset({ lon: a[0], lat: a[1] }, b);
  return normaliseDeg((Math.atan2(x, y) * 180) / Math.PI - 90);
}

/** The resize factor a handle dragged from `from` to `to` gives, about `pivot`: the ratio of their distances from it. */
export function dragScale(pivot: Position, from: readonly [number, number], to: readonly [number, number]): number {
  const a = Math.hypot(...groundOffset(pivot, from));
  return a > 0 ? Math.hypot(...groundOffset(pivot, to)) / a : 1;
}

/** Degrees clockwise a handle dragged from `from` to `to` turns about `pivot`, in (-180, 180]. */
export function dragTurnDeg(pivot: Position, from: readonly [number, number], to: readonly [number, number]): number {
  const bearing = (q: readonly [number, number]) => { const [x, y] = groundOffset(pivot, q); return (Math.atan2(x, y) * 180) / Math.PI; };
  return normaliseDeg(bearing(to) - bearing(from));
}

/** Scales the outline by `factor` and turns it `rotationDeg` clockwise (as on a compass), both about its centroid. */
export function transformRing(ring: Ring, factor: number, rotationDeg: number): Ring {
  return transformAbout(ring, ringPivot(ring), factor, rotationDeg);
}

/** An angle in degrees, folded into (-180, 180]. */
export function normaliseDeg(deg: number): number {
  const d = ((((deg + 180) % 360) + 360) % 360) - 180;
  return d === -180 ? 180 : d;
}

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function isNoAdjust(a: OutlineAdjust): boolean {
  return Math.abs(a.scale - 1) < 1e-6 && Math.abs(a.rotationDeg) < 1e-6;
}

/** `parts` are further red-line areas of a split site; they move, scale and turn with the main ring, about its centroid. */
export interface AdjustableOutline { ring: Ring; parts?: Ring[]; areaHa: number; adjust?: OutlineAdjust }

export function shiftParts(parts: Ring[] | undefined, dLon: number, dLat: number): Ring[] | undefined {
  return parts?.map(r => shiftRing(r, dLon, dLat));
}

/**
 * Brings the outline to an absolute scale and rotation, measured from the outline as first read from the plan.
 * Its area follows the scale; turning an outline does not change its area.
 */
export function adjustOutline<T extends AdjustableOutline>(outline: T, next: OutlineAdjust): T {
  const cur = outline.adjust ?? NO_ADJUST;
  const scale = clampScale(next.scale);
  const rotationDeg = normaliseDeg(next.rotationDeg);
  const factor = scale / cur.scale;
  const adjust = isNoAdjust({ scale, rotationDeg }) ? undefined : { scale, rotationDeg };
  const pivot = ringPivot(outline.ring);
  return {
    ...outline,
    ring: transformAbout(outline.ring, pivot, factor, rotationDeg - cur.rotationDeg),
    ...(outline.parts ? { parts: outline.parts.map(r => transformAbout(r, pivot, factor, rotationDeg - cur.rotationDeg)) } : {}),
    areaHa: outline.areaHa * factor * factor,
    adjust,
  };
}

export interface RestorableOutline extends AdjustableOutline { userEdited: boolean; origin?: [number, number] }

/** Records where the outline was first read, so that however it is moved later it can be put back. */
export function withOrigin<T extends RestorableOutline>(outline: T): T {
  if (outline.origin) return outline;
  const p = ringPivot(outline.ring);
  return { ...outline, origin: [p.lon, p.lat] };
}

/** The outline as first read off the plan, rebuilt from a moved, resized or turned one; null when its origin was not recorded. */
export function calculatedOutline<T extends RestorableOutline>(outline: T): T | null {
  if (!outline.userEdited && !outline.adjust) return outline;
  if (!outline.origin) return null;
  const base = outline.adjust ? adjustOutline(outline, NO_ADJUST) : outline;
  const p = ringPivot(base.ring);
  const dLon = outline.origin[0] - p.lon;
  const dLat = outline.origin[1] - p.lat;
  return { ...base, ring: shiftRing(base.ring, dLon, dLat), ...(base.parts ? { parts: shiftParts(base.parts, dLon, dLat) } : {}), userEdited: false, adjust: undefined };
}

/** Area of a closed [lon, lat] ring in hectares, on a local flat projection (accurate at the scale of a site). */
export function ringAreaHa(ring: Ring): number {
  if (ring.length < 4) return 0;
  const lat0 = (ring.reduce((sum, p) => sum + p[1], 0) / ring.length) * Math.PI / 180;
  const kx = 111_320 * Math.cos(lat0);
  const ky = 110_574;
  let twice = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    twice += x1 * kx * y2 * ky - x2 * kx * y1 * ky;
  }
  return Math.abs(twice) / 2 / 10_000;
}
